/**
 * Inviter un membre — chunk 9.AE.1.
 *
 * Réutilise le mécanisme d'authentification existant : le serveur délègue à
 * `inviteUser()`, qui gère les trois modes selon `INVITE_EMAIL_MODE` et renvoie
 * l'invité vers `/auth/accept-invite`. **Aucun mot de passe n'est créé ici**, et il
 * n'y a pas de second système parallèle.
 *
 * Les rôles proposés s'arrêtent à ce que le serveur acceptera : un `tenant_admin`
 * ne peut fabriquer ni `tenant_admin` ni `super_admin`
 * (`USERS_ROLE_ESCALATION_FORBIDDEN`). On ne propose donc pas ce qui sera refusé —
 * mais c'est le serveur qui tranche, l'interface ne fait qu'éviter l'aller-retour.
 */
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../../contexts/AuthContext";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useInviteMember, type TeamRole } from "../../../lib/queries/team";

export function InviteMemberDialog({ onClose }: { readonly onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const idTitre = useId();
  const idNom = useId();
  const idEmail = useId();
  const idRole = useId();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("broker");
  const [erreur, setErreur] = useState<string | null>(null);
  const inviter = useInviteMember();

  // Un `super_admin` peut tout assigner ; un `tenant_admin` s'arrête aux rôles non
  // administratifs. Miroir exact d'`assertCanAssignRole` côté serveur.
  const rolesPossibles: TeamRole[] =
    user?.role === "super_admin"
      ? ["tenant_admin", "broker", "client_contact"]
      : ["broker", "client_contact"];

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    try {
      await inviter.mutateAsync({
        email: email.trim(),
        full_name: fullName.trim(),
        role,
        locale: "fr",
      });
      onClose();
    } catch (err) {
      setErreur(
        getApiErrorMessage(err, normalizeLang(i18n.resolvedLanguage)) ||
          t("team.invite_dialog.error"),
      );
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTitre}
    >
      <form
        onSubmit={soumettre}
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-lg"
      >
        <h2 id={idTitre} className="text-base font-semibold text-slate-900">
          {t("team.invite_dialog.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{t("team.invite_dialog.hint")}</p>

        <label htmlFor={idNom} className="mt-4 block text-sm font-medium text-slate-700">
          {t("team.invite_dialog.name")}
        </label>
        <input
          id={idNom}
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          minLength={2}
          maxLength={120}
          autoComplete="name"
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
        />

        <label htmlFor={idEmail} className="mt-4 block text-sm font-medium text-slate-700">
          {t("team.invite_dialog.email")}
        </label>
        <input
          id={idEmail}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
        />

        <label htmlFor={idRole} className="mt-4 block text-sm font-medium text-slate-700">
          {t("team.invite_dialog.role")}
        </label>
        <select
          id={idRole}
          value={role}
          onChange={(e) => setRole(e.target.value as TeamRole)}
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
        >
          {rolesPossibles.map((r) => (
            <option key={r} value={r}>
              {t(`team.role.${r}`)}
            </option>
          ))}
        </select>

        {erreur !== null && (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {erreur}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="focus-visible:ring-brand-500 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={inviter.isPending}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 rounded-lg px-3 py-1.5 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {inviter.isPending ? t("team.invite_dialog.sending") : t("team.invite_dialog.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
